// Sekcja „Serwer” w panelu rodzica: stan połączenia, parowanie urządzenia PIN-em, odłączenie.
import { pairFailureText } from '../lib/device.ts';
import type { DeviceInfo } from '../lib/device.ts';
import type { AzureStatus } from '../speech/assessors.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';

/** Jedno zdanie o stanie serwera, napisane dla rodzica (z podpowiedzią, co zrobić). */
export function serverStatus(device: DeviceInfo | null, azure: AzureStatus | undefined, detail?: string): string {
  if (azure === undefined) return 'Serwer: sprawdzam…';
  switch (azure) {
    case 'ok':
      return device ? `Serwer: połączono ✓${device.name ? ` (urządzenie „${device.name}”)` : ''}` : 'Serwer: działa bez parowania (tryb lokalny — klucze Azure z pliku .env).';
    case 'limit':
      return 'Serwer: połączono, ale dzienny limit dokładnej oceny wymowy jest wyczerpany — wróci jutro. Do tego czasu ocenia przeglądarka.';
    case 'unpaired':
      return device ? 'Serwer: ten klucz jest nieważny albo urządzenie zostało wyłączone — sparuj je ponownie.' : 'Serwer: to urządzenie nie jest sparowane — dokładna ocena wymowy (Azure) jest wyłączona.';
    case 'off': {
      const why = detail ? ` Powód: ${detail}` : '';
      return device ? `Serwer: nie wydaje tokenu Azure — ocena idzie przez przeglądarkę.${why}` : `Serwer: brak (aplikacja działa bez backendu) — ocena idzie przez przeglądarkę.${why}`;
    }
  }
}

/** Formularz parowania pokazujemy, gdy urządzenie nie ma klucza, a Azure i tak nie działa „po staremu” (lokalnie z .env). */
export function showPairForm(device: DeviceInfo | null, azure: AzureStatus | undefined): boolean {
  return !device && azure !== 'ok';
}

export function renderPairing(ctx: AppCtx, onChange: () => void): HTMLElement {
  const status = h('p', { class: 'muted', 'data-testid': 'server-status' });
  const message = h('p', { class: 'muted', role: 'status', 'data-testid': 'pair-message' });
  const pin = h('input', { type: 'password', autocomplete: 'off', placeholder: 'PIN rodzica', 'aria-label': 'PIN rodzica', 'data-testid': 'pair-pin' });
  const name = h('input', { type: 'text', maxlength: 40, autocomplete: 'off', placeholder: 'Nazwa urządzenia, np. Tablet Ani', 'aria-label': 'Nazwa urządzenia', 'data-testid': 'pair-name' });
  let busy = false;

  const submit = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    pairBtn.disabled = true;
    message.textContent = 'Łączę z serwerem…';
    try {
      const r = await ctx.pair(pin.value, name.value);
      if (r.ok) {
        pin.value = '';
        message.textContent = r.persisted ? '✅ Sparowano. Dokładna ocena wymowy jest włączona.' : '✅ Sparowano, ale ta przeglądarka nie pozwala zapisać klucza (tryb prywatny?) — po zamknięciu karty trzeba będzie sparować ponownie.';
      } else {
        message.textContent = `❌ ${pairFailureText(r)}`;
      }
    } finally {
      busy = false;
      pairBtn.disabled = false;
      paint();
      onChange();
    }
  };
  const pairBtn = button('Sparuj to urządzenie', () => void submit(), { class: 'btn ghost', testid: 'pair-submit' });
  pin.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') void submit();
  });
  const form = h('div', { class: 'pair-form', 'data-testid': 'pair-form' }, h('div', { class: 'row' }, pin, name), pairBtn);
  let unpairing = false;
  const unpairBtn = button('Odłącz to urządzenie', () => {
    if (unpairing) return;
    if (!confirm('Odłączyć to urządzenie od serwera? Dokładna ocena wymowy przestanie działać, aż rodzic sparuje je znowu.')) return;
    unpairing = true;
    unpairBtn.disabled = true;
    void ctx.unpair().then(() => {
      unpairing = false;
      unpairBtn.disabled = false;
      if (ctx.account && !ctx.account.active()) {
        ctx.goHome(); // tryb kont: bez klucza wracamy do logowania
        return;
      }
      message.textContent = 'Odłączono to urządzenie — zwolniło też swoje miejsce na serwerze (bez internetu serwer zrobi to sam przy kolejnym parowaniu).';
      paint();
      onChange();
    });
  }, { class: 'btn link danger', testid: 'pair-unpair' });

  function paint(): void {
    const device = ctx.devices.get();
    const azure = ctx.chain().azure;
    status.textContent = serverStatus(device, azure, ctx.chain().azureDetail);
    form.hidden = !showPairForm(device, azure);
    unpairBtn.hidden = !device;
  }
  paint();
  void ctx.whenChain().then(paint);

  return h('div', { class: 'pair-box', 'data-testid': 'pair-box' }, status, form, unpairBtn, message);
}
